// Sample uploads and SDK error handling — the host-agnostic half of the
// sample library. No Node imports: the extension and the viz app (in the
// browser) both run this. Persistence lives in library.ts (extension only).
//
// Uploads run in parallel, outside any document lock, and each waits for
// `ready` (transcoding, ~7 s for a short file — spike N7), not merely
// `uploaded`: a region pointing at an untranscoded sample crashes the studio's
// audio engine. They go up UNLISTED — a producer's unreleased stems must not
// appear in Audiotool's public sample browser because they were pushed.
//
// SDK results are checked with isSdkError, not instanceof: inside Live the SDK
// is required outside our vm, so its Errors come from another realm.

import type { AudiotoolClient } from "@audiotool/nexus";

import type { PushUpload, SampleLibrary, LibrarySample } from "../../../shared/audiotool/push-reconcile.js";
import type { PushReporter } from "./progress.js";

/** An Error from any realm: instanceof, or the shape of one. */
export function isSdkError(x: unknown): x is Error {
  if (x instanceof Error) return true;
  return typeof x === "object" && x !== null && typeof (x as { message?: unknown }).message === "string" &&
    "stack" in x;
}

export function errorText(x: unknown): string {
  if (typeof x === "object" && x !== null && typeof (x as { message?: unknown }).message === "string") {
    return (x as { message: string }).message;
  }
  return String(x);
}


/** One sample in, one transcoded sample out — or an Error. */
export type SampleUploader = (
  bytes: Uint8Array,
  displayName: string,
  bpm: number | undefined,
  signal?: AbortSignal,
) => Promise<LibrarySample | Error>;

export function sdkUploader(client: Pick<AudiotoolClient, "samples">): SampleUploader {
  return async (bytes, displayName, bpm, signal) => {
    // An ArrayBuffer, not a Blob: Live's extension host has no Blob global
    // (like Headers and TextDecoder), and the SDK takes either. A standalone
    // copy, because a view into a larger (possibly shared) buffer would upload
    // the wrong bytes.
    const file = Uint8Array.from(bytes).buffer;
    const upload = await client.samples.upload(
      { file, displayName, visibility: "unlisted", ...(bpm !== undefined && bpm > 0 ? { bpm } : {}) },
      signal,
    );
    if (isSdkError(upload)) return upload;
    const meta = await upload.ready; // transcoded — `uploaded` alone is not enough
    if (isSdkError(meta)) return meta;
    return { name: meta.name, durationSeconds: meta.durationSeconds };
  };
}

export interface UploadReport {
  uploaded: string[];
  failed: Array<{ tag: string; error: string }>;
}

/**
 * Upload every sample `uploads` names that the library lacks, `concurrency` at
 * a time, recording each into `library` as it becomes ready. A failed upload
 * is reported and skipped — its regions fail to create and are retried on the
 * next push — never fatal to the rest.
 */
export async function uploadMissing(
  uploads: readonly PushUpload[],
  bytesOf: (tag: string) => Uint8Array | undefined,
  library: SampleLibrary,
  upload: SampleUploader,
  opts: { concurrency?: number; bpm?: number; signal?: AbortSignal; report?: PushReporter } = {},
): Promise<UploadReport> {
  const todo = uploads.filter((u) => library.byTag[u.tag] === undefined);
  const report: UploadReport = { uploaded: [], failed: [] };
  const say = opts.report ?? ((): void => {});
  // A sample the library already holds never leaves this machine — say so once,
  // up front, so a re-export reads as mostly-already-there rather than idle.
  for (const u of uploads) {
    if (library.byTag[u.tag] !== undefined) say({ kind: "sample", id: u.tag, displayName: u.displayName, state: "reused" });
  }
  say({ kind: "stage", stage: "uploading", done: 0, total: todo.length });
  let next = 0;
  let done = 0;
  const fail = (u: PushUpload, error: string): void => {
    report.failed.push({ tag: u.tag, error });
    say({ kind: "sample", id: u.tag, displayName: u.displayName, state: "failed" });
    console.warn(`[Audiotool] sample upload failed for ${u.tag} "${u.displayName}": ${error}`);
  };
  const worker = async (): Promise<void> => {
    while (next < todo.length) {
      if (opts.signal?.aborted) return;
      const u = todo[next++]!;
      const bytes = bytesOf(u.tag);
      if (!bytes) {
        fail(u, "no audio rendered for this sample");
      } else {
        say({ kind: "sample", id: u.tag, displayName: u.displayName, state: "uploading", bytes: bytes.byteLength });
        try {
          const r = await upload(bytes, `sb-${u.tag} ${u.displayName}`.slice(0, 120), opts.bpm, opts.signal);
          if (isSdkError(r)) {
            fail(u, errorText(r));
          } else {
            library.byTag[u.tag] = r;
            report.uploaded.push(u.tag);
            say({ kind: "sample", id: u.tag, displayName: u.displayName, state: "uploaded", bytes: bytes.byteLength });
          }
        } catch (e) {
          fail(u, errorText(e));
        }
      }
      say({ kind: "stage", stage: "uploading", done: ++done, total: todo.length });
    }
  };
  const n = Math.max(1, Math.min(opts.concurrency ?? 4, todo.length));
  await Promise.all(Array.from({ length: n }, worker));
  return report;
}
